import { useState, useEffect } from 'react';

export interface PricingData {
  currency: string;
  country: string;
  exchangeRate: number;
  basePrice: number;
  localPrice: number;
  formatted: string;
  symbol: string;
  flag: string;
}

export interface UserLocation {
  country: string;
  countryCode: string;
  currency: string;
  symbol: string;
}

export const useCurrency = () => {
  const [pricing, setPricing] = useState<PricingData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);

  const getCurrencySymbol = (currency: string): string => {
    const symbols: { [key: string]: string } = {
      'usd': '$', 'eur': '€', 'gbp': '£', 'jpy': '¥', 'cad': 'C$',
      'aud': 'A$', 'chf': 'CHF', 'cny': '¥', 'inr': '₹', 'brl': 'R$',
      'mxn': '$', 'cop': '$', 'nok': 'kr', 'sek': 'kr', 'dkk': 'kr',
      'pln': 'zł', 'czk': 'Kč', 'huf': 'Ft', 'krw': '₩', 'sgd': 'S$',
      'hkd': 'HK$', 'nzd': 'NZ$', 'zar': 'R', 'try': '₺', 'rub': '₽'
    };
    return symbols[currency.toLowerCase()] || currency.toUpperCase();
  };

  const getCountryCode = (country: string): string => {
    const countryCodes: { [key: string]: string } = {
      'United States': 'US',
      'Argentina': 'AR', 
      'Brazil': 'BR',
      'Mexico': 'MX',
      'Colombia': 'CO',
      'Spain': 'ES',
      'United Kingdom': 'GB',
      'Canada': 'CA',
      'Chile': 'CL',
      'Peru': 'PE',
      'Venezuela': 'VE',
      'Ecuador': 'EC',
      'Uruguay': 'UY',
      'Paraguay': 'PY',
      'Bolivia': 'BO'
    };
    return countryCodes[country] || 'US';
  };

  const formatPrice = (amount: number, currency: string): string => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.toUpperCase(),
        minimumFractionDigits: ['jpy', 'krw'].includes(currency.toLowerCase()) ? 0 : 2
      }).format(amount);
    } catch (error) {
      const symbol = getCurrencySymbol(currency);
      return `${symbol}${amount.toFixed(2)}`;
    }
  };

  const fetchPricing = async (): Promise<PricingData> => {
    const response = await fetch('/api/pricing/dynamic');
    if (!response.ok) {
      throw new Error('Failed to fetch pricing');
    }
    
    const data = await response.json();
    
    return {
      currency: data.currency,
      country: data.country,
      exchangeRate: data.exchangeRate,
      basePrice: data.basePrice,
      localPrice: data.localPrice,
      formatted: data.formatted || formatPrice(data.localPrice, data.currency), // Use backend formatting
      symbol: data.symbol || getCurrencySymbol(data.currency), // Use backend symbol
      flag: data.flag || '🇺🇸' // Use backend flag with US fallback
    };
  };

  const calculatePrice = (baseAmountUSD: number): string => {
    if (!pricing) return `$${baseAmountUSD.toFixed(2)}`;
    
    const localAmount = baseAmountUSD * pricing.exchangeRate;
    return formatPrice(localAmount, pricing.currency);
  };

  const calculateRawPrice = (baseAmountUSD: number): number => {
    if (!pricing) return baseAmountUSD;
    return baseAmountUSD * pricing.exchangeRate;
  };

  useEffect(() => {
    let isMounted = true;

    const loadPricing = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Check for cached data first
        const cached = sessionStorage.getItem('worldconserve_pricing');
        if (cached) {
          try {
            const cachedData = JSON.parse(cached);
            const cacheAge = Date.now() - new Date(cachedData.timestamp || 0).getTime();
            
            // Use cached data if less than 1 hour old
            if (cacheAge < 3600000) {
              if (isMounted) {
                const formattedPricing = {
                  ...cachedData,
                  formatted: cachedData.formatted || formatPrice(cachedData.localPrice, cachedData.currency),
                  symbol: cachedData.symbol || getCurrencySymbol(cachedData.currency)
                };
                setPricing(formattedPricing);
                
                // Set user location from cached data
                setUserLocation({
                  country: cachedData.country,
                  countryCode: getCountryCode(cachedData.country),
                  currency: cachedData.currency,
                  symbol: formattedPricing.symbol
                });
                
                setIsLoading(false);
              }
              return;
            }
          } catch (e) {
            console.warn('Failed to parse cached pricing data');
          }
        }

        // Fetch fresh data
        const pricingData = await fetchPricing();
        
        if (isMounted) {
          setPricing(pricingData);
          
          // Set user location
          setUserLocation({
            country: pricingData.country,
            countryCode: getCountryCode(pricingData.country),
            currency: pricingData.currency,
            symbol: pricingData.symbol
          });
          
          // Cache the result
          sessionStorage.setItem('worldconserve_pricing', JSON.stringify({
            ...pricingData,
            timestamp: new Date().toISOString()
          }));
        }

      } catch (err) {
        console.error('Currency detection failed:', err);
        
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Currency detection failed');
          
          // Fallback to USD
          const fallbackPricing = {
            currency: 'usd',
            country: 'United States',
            exchangeRate: 1.0,
            basePrice: 5.0,
            localPrice: 5.0,
            formatted: '$5.00',
            symbol: '$',
            flag: '🇺🇸'
          };
          setPricing(fallbackPricing);
          
          // Set fallback user location
          setUserLocation({
            country: fallbackPricing.country,
            countryCode: 'US',
            currency: fallbackPricing.currency,
            symbol: fallbackPricing.symbol
          });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadPricing();

    return () => {
      isMounted = false;
    };
  }, []);

  return {
    pricing,
    isLoading,
    error,
    userLocation,
    calculatePrice,
    calculateRawPrice,
    formatPrice: (amount: number, currency?: string) => 
      formatPrice(amount, currency || pricing?.currency || 'usd')
  };
};

export default useCurrency;